import "css/PreferenceForm.css"

import {Button, Checkbox, Form, Input, Radio, Row, Space, Typography} from "antd"
import {useNavigate, useParams} from "react-router-dom"
import {useEffect, useState} from "react"
import {MinusCircleOutlined, PlusOutlined} from "@ant-design/icons"
import {sessionService} from "services/session.service"
import {label} from "services/label.service"

const {Paragraph, Text} = Typography
const CheckboxGroup = Checkbox.Group

export default function PreferencesForm({form, doctor = false}) {
	const navigate = useNavigate()
	const {sessionId} = useParams()
	const [preferences, setPreferences] = useState(null)
	const [showPrescription, setShowPrescription] = useState(false)

	const updatePreferences = () => sessionService.getSessionDetails(sessionId)
	.then(data => data.preferences)
	.then(getPresets)
	.then(setPreferences)

	useEffect(updatePreferences, [sessionId])
	useEffect(() => form.resetFields(), [preferences, form])

	const getPresets = (preferences) => {
		if (preferences) {
			const values = {
				goals: preferences.goals,
				sideEffectsToAvoid: preferences.sideEffectsToAvoid,
				thinkAboutMethod: preferences.thinkAboutMethod,
				drugsTakenBefore: [],
				method: preferences.thinkAboutMethod ? preferences.methodPreferences : [],
				other: preferences.otherRemarks
			}
			setShowPrescription(preferences.thinkAboutMethod)
			preferences.drugsTakenBefore.forEach(d => {
				values.drugsTakenBefore.push(d.drug)
				values[d.drug] = {wouldTakeAgain: d.wouldTakeAgain}
			})
			return values
		} else {
			return null
		}
	}

	const submit = (values) => {
		const preferences = {
			goals: values.goals,
			drugsTakenBefore: values.drugsTakenBefore?.map(drug => {
				return {drug: drug, wouldTakeAgain: values[drug].wouldTakeAgain}
			}) || [],
			otherDrugsTaken: [],
			sideEffectsToAvoid: values.sideEffectsToAvoid,
			otherSideEffectsToAvoid: [],
			thinkAboutMethod: values.thinkAboutMethod,
			methodPreferences: values.thinkAboutMethod ? values.method : [],
			otherRemarks: values.other
		}
		sessionService.providePatientPreferences(sessionId, preferences)
		.then(() => {
			if (doctor) {
				navigate(`/doctor/session/${sessionId}`)
			} else {
				navigate(`/login`)
			}
		})
	}

	let prescription = <div></div>
	if (showPrescription) {
		prescription =
			<div>
				<Paragraph>
					<Text strong>
						Wie können Sie sich vorstellen das Medikament verabreicht zu bekommen?
					</Text>
				</Paragraph>
				<Form.Item name="method">
					<CheckboxGroup>
						<Space direction="vertical" size="large">
							<Row>
								<Checkbox value="PILL">
									<Text>
										Tablette
									</Text>
								</Checkbox>
							</Row>
							<Row>
								<Checkbox value="DROPS">
									<Text>
										Tropfen
									</Text>
								</Checkbox>
							</Row>
							<Row>
								<Checkbox value="SYRINGES">
									<Text>
										Spritzen (z.B. einmal alle vier Wochen)
									</Text>
								</Checkbox>
							</Row>
						</Space>
					</CheckboxGroup>
				</Form.Item>
			</div>
	}

	return (
		<div>

			<div className="ant-descriptions-title titleText">Patienten Präferenzen</div>

			<Form
				form={form}
				name="patientPreferences"
				onFinish={submit}
				onSubmit={e => e.preventDefault()}
				requiredMark={false}
				initialValues={preferences}
			>

				<div className="preferences patprefs behandlungsziele">
					<details open>
						<summary>
							<Text strong={true}>
								Was möchte ich durch die Behandlung erreichen? (Behandlungsziele)
							</Text>
						</summary>

						<div className="description">
							<Form.List name="goals">
								{(fields, {add, remove}) => (
									<>
										{fields.map(({key, name, fieldKey, ...restField}) => (
											<Space key={key} style={{display: 'flex', marginBottom: 8}} align="baseline">
												<Form.Item
													{...restField}
													name={name}
													fieldKey={fieldKey}
													rules={[{required: true, message: "Gib ein Ziel an"}]}
												>
													<Input placeholder="Ziel" style={{width: 400}}/>
												</Form.Item>
												<MinusCircleOutlined onClick={() => remove(name)}/>
											</Space>
										))}
										<Form.Item>
											<Button type="dashed" onClick={() => add()} icon={<PlusOutlined/>}>
												Ziel hinzufügen
											</Button>
										</Form.Item>
									</>
								)}
							</Form.List>
						</div>
					</details>
				</div>


				<div className="preferences patprefs medikamente">
					<details open>
						<summary>
							<Text strong={true}>
								Welche Medikamente habe ich in der Vergangenheit schon eingenommen? Würde ich diese noch einmal einnehmen?
							</Text>
						</summary>
						<div className="description">
							<Paragraph>
								<Text>
									Mit welchen Medikamenten wurden Sie in der Vergangenheit bereits behandelt?
									Bitte wählen Sie aus der folgenden Liste aus (markieren Sie zusätzlich, ob Sie das Medikament noch
									einmal
									einnehmen würden).
								</Text>
							</Paragraph>
							<Form.Item name="drugsTakenBefore">
								<CheckboxGroup className="drug-preferences">
									<DrugPreference name="AMISULPRID" form={form}/>
									<DrugPreference name="ARIPIPRAZOL" form={form}/>
									<DrugPreference name="CARIPRAZIN" form={form}/>
									<DrugPreference name="CLOZAPIN" form={form}/>
									<DrugPreference name="HALOPERIDOL" form={form}/>
									<DrugPreference name="OLANZAPIN" form={form}/>
									<DrugPreference name="PALIPERIDON" form={form}/>
									<DrugPreference name="RISPERIDON" form={form}/>
									<DrugPreference name="PERPHENAZIN" form={form}/>
									<DrugPreference name="QUETIAPIN" form={form}/>
									<DrugPreference name="SERTINDOL" form={form}/>
									<DrugPreference name="ZIPRASIDON" form={form}/>
								</CheckboxGroup>
							</Form.Item>
						</div>
					</details>
				</div>

				<div className="preferences patprefs nebenwirkungen">
					<details open>
						<summary>
							<Text strong>
								Welche Nebenwirkungen sollen vermieden werden?
							</Text>
						</summary>
						<div className="description">
							<Paragraph>
								<Text>
									Das sind die sechs häufige Nebenwirkungen. Bitte wählen Sie zwei davon aus, die Sie unbedingt vermeiden
									wollen.
								</Text>
							</Paragraph>

							<Form.Item name="sideEffectsToAvoid">
								<CheckboxGroup>
									<Space direction="vertical" size="large">
										<Row>
											<Checkbox value="ANTICHOLINERGIC">
												<Text>
													Mundtrockenheit, verschwommenes Sehen, Verstopfung (sog. anticholinerge Nebenwirkungen)
												</Text>
											</Checkbox>
										</Row>
										<Row>
											<Checkbox value="PARKINSON">
												<Text>
													Steifigkeit der Muskulatur, Bewegungsstörungen, Zittern (sog. Parkinsonsymptome)
												</Text>
											</Checkbox>
										</Row>
										<Row>
											<Checkbox value="HYPERPROLACTINEMIA">
												<Text>
													weniger Lust auf Sex, sexuelle Funktionsstörung, Menstruationsbeschwerden
												</Text>
											</Checkbox>
										</Row>
										<Row>
											<Checkbox value="WEIGHT_GAIN">
												<Text>
													Gewichtszunahme
												</Text>
											</Checkbox>
										</Row>
										<Row>
											<Checkbox value="FATIGUE">
												<Text>
													Müdigkeit
												</Text>
											</Checkbox>
										</Row>
										<Row>
											<Checkbox value="RESTLESS">
												<Text>
													Unruhige Beine
												</Text>
											</Checkbox>
										</Row>
									</Space>
								</CheckboxGroup>
							</Form.Item>
						</div>
					</details>
				</div>

				<div className="preferences patprefs verabreichung">
					<details open>
						<summary>
							<Text strong>
								Medikamente können als Tabletten, Tropfen oder Spritze (in der Regel mehrwöchige Abstände) verabreicht
								werden. Wollen Sie sich bereits jetzt darüber Gedanken machen?
							</Text>
						</summary>
						<div className="description">
							<Form.Item name="thinkAboutMethod">
								<Radio.Group onChange={e => setShowPrescription(e.target.value)}>
									<Radio value={true}>Ja</Radio>
									<Radio value={false}>Nein</Radio>
								</Radio.Group>
							</Form.Item>

							{prescription}
						</div>
					</details>
				</div>

				<div className="preferences patprefs arztgespraech">
					<details open>
						<summary>
							<Text strong>
								Was ist mir sonst noch wichtig im Arztgespräch?
							</Text>
						</summary>
						<div className="description">
							<Form.List name="other">
								{(fields, {add, remove}) => (
									<>
										{fields.map(({key, name, fieldKey, ...restField}) => (
											<Space key={key} style={{display: 'flex', marginBottom: 8}} align="baseline">
												<Form.Item
													{...restField}
													name={name}
													fieldKey={fieldKey}
													rules={[{required: true, message: "Fülle das Feld aus oder entferne es"}]}
												>
													<Input placeholder="Sonst noch wichtig" style={{width: 400}}/>
												</Form.Item>
												<MinusCircleOutlined onClick={() => remove(name)}/>
											</Space>
										))}
										<Form.Item>
											<Button type="dashed" onClick={() => add()} icon={<PlusOutlined/>}>
												Sonst noch wichtig
											</Button>
										</Form.Item>
									</>
								)}
							</Form.List>
						</div>
					</details>
				</div>
			</Form>

		</div>
	)
}

function DrugPreference({name, form}) {

	const [checked, setChecked] = useState(form.getFieldValue("drugsTakenBefore")?.includes(name))
	const onCheckboxChange = e => setChecked(e.target.checked)

	return (
		<div className="drug-preference-item">
			<Checkbox value={name} checked={checked} onChange={onCheckboxChange}>
				{`${label.get(name)} (z.B. ${label.get(`${name}_N`)})`}
			</Checkbox>
			<div className={(checked ? "visible" : "invisible")}>
				<Text>Würde ich nocheinmal nehmen</Text>
				<Form.Item name={[name, "wouldTakeAgain"]} rules={[{required: checked, message: 'Bitte auswählen!'}]}>
					<Radio.Group disabled={!checked}>
						<Radio value={true}>Ja</Radio>
						<Radio value={false}>Nein</Radio>
					</Radio.Group>
				</Form.Item>
			</div>
		</div>
	)
}
